/**
 * OrgSwitcher - Organisation dropdown menu for Header
 */

import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Building2, ChevronDown, Settings, Network } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { OrganisationOption, User } from '../types'

interface OrgSwitcherProps {
  organisations: OrganisationOption[]
  currentOrganisationId?: string
  onSelectOrganisation?: (org: OrganisationOption) => void
  onOrgClick?: () => void
  user?: User | null
}

export function OrgSwitcher({
  organisations,
  currentOrganisationId,
  onSelectOrganisation,
  onOrgClick,
  user,
}: OrgSwitcherProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const canSwitch = Array.isArray(organisations) && organisations.length > 1 && !!onSelectOrganisation
  const currentLabel =
    organisations.find((o) => String(o.id) === String(currentOrganisationId))?.name ||
    user?.organisation ||
    'Organisation'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 text-xs rounded-md border transition-colors',
          'border-border',
          'text-foreground hover:bg-muted'
        )}
        title={currentLabel}
        aria-label="Organisation"
      >
        <Building2 className="w-3.5 h-3.5 text-slate-400" />
        <span className="hidden sm:block max-w-40 truncate">{currentLabel}</span>
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 w-64 bg-popover border border-border rounded-lg shadow-lg z-50 py-1">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs font-semibold text-foreground truncate">{currentLabel}</p>
            <p className="text-[10px] text-muted-foreground">Current organisation</p>
          </div>

          <div className="py-1">
            <button
              onClick={() => { setOpen(false); onOrgClick?.() }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-foreground hover:bg-muted"
            >
              <Settings className="w-3.5 h-3.5 text-slate-400" />
              <span>Organisation Settings</span>
            </button>
            <Link
              to="/org-structure"
              onClick={() => setOpen(false)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-foreground hover:bg-muted"
            >
              <Network className="w-3.5 h-3.5 text-slate-400" />
              <span>Organisation Chart</span>
            </Link>
          </div>

          {canSwitch && (
            <>
              <div className="border-t border-border my-1" />
              <div className="px-3 py-1">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Switch Organisation
                </p>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {organisations.map((org) => {
                  const isCurrent = String(org.id) === String(currentOrganisationId)
                  return (
                    <button
                      key={org.id}
                      onClick={() => { setOpen(false); onSelectOrganisation?.(org) }}
                      className={cn(
                        'w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left',
                        'hover:bg-muted',
                        isCurrent && 'bg-teal-50 dark:bg-teal-900/20'
                      )}
                    >
                      <span className={cn('truncate', isCurrent ? 'font-semibold text-teal-700 dark:text-teal-300' : 'text-foreground')}>
                        {org.name}
                      </span>
                      {isCurrent && <span className="text-[10px] text-teal-600 dark:text-teal-400">Current</span>}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
